import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { SUPER_NAV } from "./SuperDashboard";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FileSpreadsheet, ChevronRight, Send, X } from "lucide-react";

const STATUS_STYLES = {
  submitted: "bg-blue-100 text-blue-800",
  quoted:    "bg-emerald-100 text-emerald-800",
  closed:    "bg-slate-100 text-slate-700",
  cancelled: "bg-rose-100 text-rose-800",
};

export default function SuperSchoolRfqs() {
  const [rfqs, setRfqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/school-rfqs", {
        params: { status: statusFilter || undefined },
      });
      setRfqs(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [statusFilter]);

  return (
    <DashboardLayout nav={SUPER_NAV} title="School RFQs">
      <div className="flex items-center gap-3 mb-4">
        <select data-testid="filter-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                className="rounded-md border border-slate-300 py-2 px-3 text-sm">
          <option value="">All statuses</option>
          <option value="submitted">Submitted</option>
          <option value="quoted">Quoted</option>
          <option value="closed">Closed</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <div className="text-sm text-slate-500">{rfqs.length} RFQ(s)</div>
      </div>

      {loading ? (
        <div className="text-slate-500">Loading…</div>
      ) : rfqs.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-slate-500">
          <FileSpreadsheet size={36} className="mx-auto text-slate-300 mb-3"/>
          No RFQs.
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">RFQ #</th>
                <th className="text-left px-4 py-3">School</th>
                <th className="text-left px-4 py-3">Items</th>
                <th className="text-left px-4 py-3">Submitted</th>
                <th className="text-left px-4 py-3">Due</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rfqs.map((r) => (
                <tr key={r.id} data-testid={`rfq-row-${r.id}`} className="hover:bg-slate-50/50 cursor-pointer" onClick={() => setActive(r)}>
                  <td className="px-4 py-3 font-mono text-brand-navy font-semibold">{r.rfq_number}</td>
                  <td className="px-4 py-3 text-slate-700">{r.school_name}</td>
                  <td className="px-4 py-3 text-slate-600">{(r.items||[]).length}</td>
                  <td className="px-4 py-3 text-slate-600">{r.submitted_at ? new Date(r.submitted_at).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{r.delivery_required_by || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_STYLES[r.status] || "bg-slate-100 text-slate-700"}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right"><ChevronRight size={14} className="text-slate-400 inline"/></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {active && <RfqRespond rfq={active} close={() => setActive(null)} onSaved={() => { setActive(null); load(); }}/>}
    </DashboardLayout>
  );
}

function RfqRespond({ rfq, close, onSaved }) {
  const [status, setStatus] = useState("quoted");
  const [amount, setAmount] = useState(rfq.quote?.amount || "");
  const [terms, setTerms] = useState(rfq.quote?.terms || "");
  const [validUntil, setValidUntil] = useState(rfq.quote?.valid_until || "");
  const [notes, setNotes] = useState(rfq.admin_notes || "");
  const [submitting, setSubmitting] = useState(false);
  const [converting, setConverting] = useState(false);
  const alreadyConverted = !!rfq.converted_order_id;

  const convertToPO = async () => {
    if (!window.confirm("Create a Purchase Order from this quoted RFQ? This action is idempotent — running it twice returns the same PO.")) return;
    setConverting(true);
    try {
      const r = await api.post(`/admin/school-rfqs/${rfq.id}/convert-to-order`);
      const po = r.data.order;
      toast.success(`PO ${po.order_number} created for ${po.school_name}`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setConverting(false); }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/admin/school-rfqs/${rfq.id}/respond`, {
        status,
        quote_amount: amount ? Number(amount) : null,
        quote_terms: terms || null,
        quote_valid_until: validUntil || null,
        admin_notes: notes || null,
      });
      toast.success(`RFQ ${rfq.rfq_number} → ${status}`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="rfq-respond">
      <div className="flex-1 bg-black/40" onClick={close}/>
      <aside className="w-full sm:w-[520px] h-full bg-white flex flex-col overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div>
            <div className="text-xs text-slate-500">School RFQ</div>
            <div className="font-heading font-semibold">{rfq.rfq_number}</div>
            <div className="text-xs text-slate-500">{rfq.school_name}</div>
          </div>
          <button onClick={close}><X size={16}/></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="rounded-lg border border-slate-200">
            <div className="px-3 py-2 bg-slate-50 text-xs font-semibold text-slate-600 uppercase tracking-wider">Items requested</div>
            <div className="divide-y divide-slate-100">
              {(rfq.items || []).map((it, idx) => (
                <div key={idx} className="p-3 flex items-center gap-3 text-sm">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-800 truncate">{it.product_name}</div>
                    <div className="text-xs text-slate-500">{it.product_category}</div>
                    {it.notes && <div className="text-xs text-slate-500 italic">{it.notes}</div>}
                  </div>
                  <div className="text-right"><div className="text-[11px] text-slate-500">Qty</div><div className="font-bold">{it.qty}</div></div>
                </div>
              ))}
            </div>
          </div>
          {rfq.notes && (
            <div>
              <div className="text-xs font-semibold text-slate-500">School notes</div>
              <div className="text-sm text-slate-800 whitespace-pre-line">{rfq.notes}</div>
            </div>
          )}

          <div className="pt-2 border-t border-slate-200 space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-600">Response status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="resp-status"
                      className="mt-1 w-full rounded-md border border-slate-300 py-2 px-3 text-sm">
                <option value="quoted">Quoted (attach quote below)</option>
                <option value="closed">Closed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            {status === "quoted" && (
              <>
                <div>
                  <label className="text-xs font-semibold text-slate-600">Quote total (INR)</label>
                  <input type="number" min={0} value={amount} onChange={(e) => setAmount(e.target.value)}
                         data-testid="resp-amount"
                         className="mt-1 w-full rounded-md border border-slate-300 py-2 px-3 text-sm"/>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600">Terms</label>
                  <textarea rows={2} value={terms} onChange={(e) => setTerms(e.target.value)}
                            data-testid="resp-terms"
                            className="mt-1 w-full rounded-md border border-slate-300 py-2 px-3 text-sm"/>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600">Valid until</label>
                  <input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)}
                         data-testid="resp-valid"
                         className="mt-1 w-full rounded-md border border-slate-300 py-2 px-3 text-sm"/>
                </div>
              </>
            )}
            <div>
              <label className="text-xs font-semibold text-slate-600">Internal notes</label>
              <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                        data-testid="resp-notes"
                        className="mt-1 w-full rounded-md border border-slate-300 py-2 px-3 text-sm"/>
            </div>
          </div>
        </div>
        <div className="p-4 border-t border-slate-200 space-y-2">
          {rfq.status === "quoted" && !alreadyConverted && (
            <button onClick={convertToPO} disabled={converting}
                    data-testid="btn-convert-to-po"
                    className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-md bg-brand-green text-white font-semibold hover:bg-brand-greenDark disabled:bg-slate-300">
              {converting ? "Creating PO…" : "Convert to Purchase Order"}
            </button>
          )}
          {alreadyConverted && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2 text-emerald-800 text-xs text-center">
              Converted to <b>{rfq.converted_order_number}</b> on {rfq.converted_at?.slice(0, 10)}
            </div>
          )}
          <button onClick={submit} disabled={submitting} data-testid="btn-submit-response"
                  className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-md bg-brand-blue text-white font-semibold disabled:bg-slate-300">
            <Send size={14}/> {submitting ? "Sending…" : "Send response"}
          </button>
        </div>
      </aside>
    </div>
  );
}
